import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  _Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { IoTrashOutline as DeleteIcon } from 'react-icons/io5';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import callApi from 'client/util/apiCaller';
import { useDispatch, useSelector } from 'react-redux';
import { canManageOrg } from '../../../Auth/AuthReducer';

const messages = defineMessages({
  title: {
    id: 'DeleteRespondents.Title',
    defaultMessage: 'Delete Respondents',
  },
  description: {
    id: 'DeleteRespondents.Description',
    defaultMessage:
      'Remove all responses collected from a single email address or respondent ID from all your surveys to help ensure GDPR compliance.',
  },
  searchPlaceholder: {
    id: 'DeleteRespondents.SearchPlaceholder',
    defaultMessage: 'Enter email address or respondent ID',
  },
  searchButton: {
    id: 'DeleteRespondents.SearchButton',
    defaultMessage: 'Search',
  },
  noResults: {
    id: 'DeleteRespondents.NoResults',
    defaultMessage: 'No responses found for this search.',
  },
  resultsTable: {
    id: 'DeleteRespondents.ResultsTable',
    defaultMessage: 'Search Results',
  },
  emailColumn: {
    id: 'DeleteRespondents.EmailColumn',
    defaultMessage: 'Email',
  },
  respondentIdColumn: {
    id: 'DeleteRespondents.RespondentIdColumn',
    defaultMessage: 'Respondent ID',
  },
  surveysColumn: {
    id: 'DeleteRespondents.SurveysColumn',
    defaultMessage: 'Surveys',
  },
  surveyCountColumn: {
    id: 'DeleteRespondents.SurveyCountColumn',
    defaultMessage: 'Responses (hover for surveys)',
  },
  actionsColumn: {
    id: 'DeleteRespondents.ActionsColumn',
    defaultMessage: 'Actions',
  },
  deleteButton: {
    id: 'DeleteRespondents.DeleteButton',
    defaultMessage: 'Delete',
  },
  deleteConfirmTitle: {
    id: 'DeleteRespondents.DeleteConfirmTitle',
    defaultMessage: 'Confirm Deletion',
  },
  deleteConfirmMessage: {
    id: 'DeleteRespondents.DeleteConfirmMessage',
    defaultMessage:
      'Are you sure you want to delete this respondent and all their responses? This action cannot be undone.',
  },
  deleteConfirmButton: {
    id: 'DeleteRespondents.DeleteConfirmButton',
    defaultMessage: 'Delete Respondent',
  },
  cancelButton: {
    id: 'DeleteRespondents.CancelButton',
    defaultMessage: 'Cancel',
  },
  deleteSuccess: {
    id: 'DeleteRespondents.DeleteSuccess',
    defaultMessage: 'Respondent deleted successfully.',
  },
  deleteSuccessWithCount: {
    id: 'DeleteRespondents.DeleteSuccessWithCount',
    defaultMessage: 'Successfully deleted {deletedCount} responses from {email}',
  },
  deleteError: {
    id: 'DeleteRespondents.DeleteError',
    defaultMessage: 'Error deleting respondent. Please try again.',
  },
  searchError: {
    id: 'DeleteRespondents.SearchError',
    defaultMessage: 'Error searching for responses. Please try again.',
  },
  loading: {
    id: 'DeleteRespondents.Loading',
    defaultMessage: 'Loading...',
  },
  emailLabel: {
    id: 'DeleteRespondents.EmailLabel',
    defaultMessage: 'Email:',
  },
  surveysLabel: {
    id: 'DeleteRespondents.SurveysLabel',
    defaultMessage: 'Surveys:',
  },
  totalResponsesLabel: {
    id: 'DeleteRespondents.TotalResponsesLabel',
    defaultMessage: 'Total Responses:',
  },
  respondentIdsLabel: {
    id: 'DeleteRespondents.RespondentIdsLabel',
    defaultMessage: 'Respondent IDs:',
  },
});

function DeleteRespondentsPage() {
  const { formatMessage } = useIntl();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const dispatch = useDispatch();
  const canManage = useSelector(canManageOrg);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await callApi(
        dispatch,
        `respondents/search?q=${encodeURIComponent(searchTerm.trim())}`,
      );

      if (response.success) {
        setSearchResults(response.data || []);
        if (response.data && response.data.length === 0) {
          setMessage({ type: 'info', text: formatMessage(messages.noResults) });
        }
      } else {
        setMessage({ type: 'error', text: formatMessage(messages.searchError) });
      }
    } catch (error) {
      console.error('Search error:', error);
      setMessage({ type: 'error', text: formatMessage(messages.searchError) });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = respondent => {
    setSelectedResponse(respondent);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedResponse) return;

    setDeleteLoading(true);
    try {
      // Use bulk delete by email since we're deleting all responses for a respondent
      const response = await callApi(
        dispatch,
        `respondents/email/${encodeURIComponent(selectedResponse.email)}`,
        null,
        'DELETE',
      );

      if (response.success) {
        setMessage({
          type: 'success',
          text: formatMessage(messages.deleteSuccessWithCount, {
            deletedCount: response.deletedCount,
            email: selectedResponse.email,
          }),
        });
        // Remove all entries for this email from the results
        setSearchResults(prev => prev.filter(r => r.email !== selectedResponse.email));
        setDeleteDialogOpen(false);
        setSelectedResponse(null);
      } else {
        setMessage({ type: 'error', text: formatMessage(messages.deleteError) });
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage({ type: 'error', text: formatMessage(messages.deleteError) });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedResponse(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {formatMessage(messages.title)}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {formatMessage(messages.description)}
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              label={formatMessage(messages.searchPlaceholder)}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              disabled={loading || !canManage}
              size="small"
              sx={{ mt: 0 }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={!searchTerm.trim() || loading || !canManage}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
            >
              {loading ? formatMessage(messages.loading) : formatMessage(messages.searchButton)}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {searchResults.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {formatMessage(messages.resultsTable)}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{formatMessage(messages.emailColumn)}</TableCell>
                    <TableCell align="right">{formatMessage(messages.surveyCountColumn)}</TableCell>
                    <TableCell align="right">{formatMessage(messages.actionsColumn)}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map(respondent => (
                    <TableRow key={respondent.email}>
                      <TableCell>{respondent.email}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={respondent.surveyNames.join(', ')} placement="top" arrow>
                          <Box component="span" sx={{ cursor: 'help' }}>
                            {respondent.surveyNames.length}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(respondent)}
                          title={formatMessage(messages.deleteButton)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{formatMessage(messages.deleteConfirmTitle)}</DialogTitle>
        <DialogContent>
          <Typography>{formatMessage(messages.deleteConfirmMessage)}</Typography>
          {selectedResponse && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{formatMessage(messages.emailLabel)}</strong> {selectedResponse.email}
              </Typography>
              <Typography variant="body2">
                <strong>{formatMessage(messages.surveysLabel)}</strong>{' '}
                {selectedResponse.surveyNames.join(', ')}
              </Typography>
              <Typography variant="body2">
                <strong>{formatMessage(messages.totalResponsesLabel)}</strong>{' '}
                {selectedResponse.totalSurveyCount}
              </Typography>
              <Typography variant="body2">
                <strong>{formatMessage(messages.respondentIdsLabel)}</strong>{' '}
                {selectedResponse.personIds.join(', ')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            {formatMessage(messages.cancelButton)}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            {deleteLoading
              ? formatMessage(messages.loading)
              : formatMessage(messages.deleteConfirmButton)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DeleteRespondentsPage;
